import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Play, SpeakerSimpleHigh, SpeakerSimpleSlash } from "@phosphor-icons/react";

// Fullscreen pinned preview overlay. Opened when the user long-presses
// a media card (Apple-TV / YouTube-style). Renders via portal to
// document.body because page containers use `animate-fade-up` which
// creates a transform-based containing block and breaks `position:fixed`
// for descendants.
//
// Props:
//   youtube_id, title, subtitle, poster — describing the previewed media
//   onClose                            — close the overlay
//   onPlayFull                         — user wants to actually watch this
export default function LongPressPreview({ youtube_id, title, subtitle, poster, onClose, onPlayFull }) {
  const [muted, setMuted] = useState(true);
  const iframeRef = useRef(null);

  // Close on Escape so desktop users can dismiss with one key.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleMute = () => {
    const fr = iframeRef.current;
    setMuted((m) => {
      const next = !m;
      try {
        fr?.contentWindow?.postMessage(
          JSON.stringify({ event: "command", func: next ? "mute" : "unMute", args: [] }),
          "*"
        );
      } catch (_) {}
      return next;
    });
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-ink/95 backdrop-blur-md grid place-items-center animate-fade-up"
      data-testid="longpress-preview"
      onClick={onClose}
    >
      <div
        className="relative w-[92vw] max-w-3xl lg:max-w-none lg:!w-screen rounded-3xl overflow-hidden bg-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aspect-video relative bg-black">
          {youtube_id ? (
            <iframe
              ref={iframeRef}
              title={`${title} preview`}
              src={`https://www.youtube.com/embed/${youtube_id}?autoplay=1&mute=1&controls=0&modestbranding=1&playsinline=1&rel=0&loop=1&enablejsapi=1&playlist=${youtube_id}`}
              className="absolute inset-0 w-full h-full pointer-events-none"
              allow="autoplay; encrypted-media"
              frameBorder="0"
            />
          ) : (
            poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="longpress-preview-close"
            aria-label="Close preview"
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-ink/80 hover:bg-ink text-cream grid place-items-center backdrop-blur-sm shadow-lg"
          >
            <X size={16} weight="bold" />
          </button>
          {youtube_id && (
            <button
              type="button"
              onClick={toggleMute}
              data-testid="longpress-preview-mute"
              aria-label={muted ? "Unmute preview" : "Mute preview"}
              className="absolute top-3 left-3 w-10 h-10 rounded-full bg-ink/80 hover:bg-ink text-cream grid place-items-center backdrop-blur-sm shadow-lg"
            >
              {muted ? <SpeakerSimpleSlash size={16} weight="bold" /> : <SpeakerSimpleHigh size={16} weight="bold" />}
            </button>
          )}
        </div>
        <div className="p-5 bg-forest text-cream">
          <h3 className="font-heading text-2xl leading-tight truncate">{title}</h3>
          {subtitle && <p className="text-cream/70 text-sm mt-1 truncate">{subtitle}</p>}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { onClose && onClose(); onPlayFull && onPlayFull(); }}
              data-testid="longpress-preview-play"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 h-11 text-terracotta hover:text-terracotta/80 font-bold text-[11px] tracking-[0.22em] uppercase shadow-lg"
            >
              <Play size={14} weight="fill" /> Watch full
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="longpress-preview-cancel"
              className="px-5 h-11 rounded-full bg-cream/15 hover:bg-cream/25 text-cream font-bold text-[11px] tracking-[0.22em] uppercase"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
